import { useState, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useApp, Icon, Button, Card, Pill, NakedButton } from '@/components/ds';
import { Segmented, CodeBlock, CopyChip, Method, Toggle } from '@/components/code';
import { jsonStr } from '@/lib/codegen';
import { SectionHead } from '@/containers/home';
import type { Go } from '@/lib/types';

// agents.jsx — AI & Agents hub: MCP server, live agent demo, agent-ready principles, prompts, guardrails.

// ---- Scripted agent demo ------------------------------------------------
interface AgentStep {
  role: 'user' | 'assistant' | 'tool';
  text?: string;
  tool?: string;
  args?: Record<string, unknown>;
  result?: Record<string, unknown>;
}

const AGENT_SCRIPT: AgentStep[] = [
  { role: 'user', text: 'Pay Flour Power Supply $1,250 by ACH, next Tuesday.' },
  { role: 'assistant', text: 'On it — let me confirm and schedule it.' },
  {
    role: 'tool',
    tool: 'check_eligibility',
    args: { entityId: 'me' },
    result: { canSendDomesticPayment: { allowed: true, missingFields: [] } },
  },
  {
    role: 'tool',
    tool: 'list_delivery_methods',
    args: { entityId: 'me', query: 'Flour Power' },
    result: { id: 'acct_7c4b1', ownershipType: 'external', counterPartyName: 'Flour Power Supply Co.', type: 'ach' },
  },
  {
    role: 'tool',
    tool: 'create_payment',
    args: { amount: 125000, originatingAccountId: 'acct_9a2f7', receivingAccountId: 'acct_7c4b1', deductionDate: '2026-06-10' },
    result: { id: 'pay_3x9k2v', status: 'scheduled', deliveryDate: '2026-06-13' },
  },
  { role: 'assistant', text: 'Scheduled — $1,250.00 to Flour Power Supply Co. by ACH.' },
];

interface ToolCardProps {
  step: AgentStep;
  animate?: boolean;
  defaultOpen?: boolean;
}

function ToolCard({ step, animate = true, defaultOpen = true }: ToolCardProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      style={{
        background: '#1c1e29',
        border: '1px solid #2c2f3d',
        borderRadius: 10,
        overflow: 'hidden',
        flexShrink: 0,
        animation: animate ? 'om-rise .3s ease both' : 'none',
      }}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          background: 'transparent',
          border: 'none',
          borderBottom: open ? '1px solid #2c2f3d' : 'none',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <Icon name="fast" size={14} color="#b79eff" />
        <span className="om-mono" style={{ fontSize: 12.5, color: '#cdd2dc', fontWeight: 600, whiteSpace: 'nowrap' }}>
          {step.tool}
        </span>
        <span className="om-mono" style={{ fontSize: 11, color: '#5c6472', whiteSpace: 'nowrap' }}>
          tool call
        </span>
        <div style={{ flex: 1 }} />
        <span className="om-mono" style={{ fontSize: 11, color: '#8fd6a8', whiteSpace: 'nowrap' }}>
          ● 200
        </span>
        <Icon name={open ? 'chevron-up' : 'chevron-down'} size={14} color="#5c6472" />
      </button>
      {open && (
        <div style={{ padding: '10px 12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <div
              className="om-mono"
              style={{ fontSize: 10, color: '#5c6472', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.05em' }}
            >
              args
            </div>
            <pre className="om-mono" style={{ margin: 0, fontSize: 11.5, color: '#8fb4fa', whiteSpace: 'pre-wrap' }}>
              {jsonStr(step.args)}
            </pre>
          </div>
          <div>
            <div
              className="om-mono"
              style={{ fontSize: 10, color: '#5c6472', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '.05em' }}
            >
              result
            </div>
            <pre className="om-mono" style={{ margin: 0, fontSize: 11.5, color: '#8fd6a8', whiteSpace: 'pre-wrap' }}>
              {jsonStr(step.result)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

interface AgentChatProps {
  height?: number | 'auto';
  instant?: boolean;
  summary?: boolean;
  collapsedTools?: boolean;
}

function AgentChat({ height = 480, instant = false, summary = true, collapsedTools = false }: AgentChatProps) {
  const auto = height === 'auto';
  const [n, setN] = useState(instant ? AGENT_SCRIPT.length : 0);
  const [typing, setTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (instant || n >= AGENT_SCRIPT.length) return;
    const next = AGENT_SCRIPT[n];
    const delay = next.role === 'tool' ? 850 : 1100;
    setTyping(next.role === 'assistant');
    const id = setTimeout(() => {
      setTyping(false);
      setN(n + 1);
    }, delay);
    return () => clearTimeout(id);
  }, [n, instant]);
  useEffect(() => {
    if (!auto && scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [n, typing, auto]);
  const replay = () => {
    setN(0);
  };
  const done = n >= AGENT_SCRIPT.length;
  return (
    <div
      style={{
        background: '#16171f',
        borderRadius: 16,
        border: '1px solid #2c2f3d',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        ...(auto ? {} : { height }),
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderBottom: '1px solid #2c2f3d' }}>
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: 7,
            background: 'var(--accent)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon name="ai-fill" size={16} color="#fff" />
        </div>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>Melio agent</span>
        <span className="om-mono" style={{ fontSize: 11, color: '#5c6472', whiteSpace: 'nowrap' }}>
          via MCP · test mode
        </span>
        <div style={{ flex: 1 }} />
        {!instant && (
          <button
            onClick={replay}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              background: 'transparent',
              border: '1px solid #2c2f3d',
              borderRadius: 7,
              color: '#9aa3b2',
              fontSize: 12,
              fontWeight: 600,
              padding: '5px 10px',
              cursor: 'pointer',
            }}
          >
            <Icon name="arrow-left" size={13} color="#9aa3b2" />
            Replay
          </button>
        )}
      </div>
      <div
        ref={scrollRef}
        style={{
          flex: auto ? 'none' : 1,
          overflowY: auto ? 'visible' : 'auto',
          padding: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
        }}
      >
        {AGENT_SCRIPT.slice(0, n).map((s, i) => {
          if (s.role === 'user')
            return (
              <div
                key={i}
                style={{
                  alignSelf: 'flex-end',
                  maxWidth: '82%',
                  flexShrink: 0,
                  background: 'var(--accent)',
                  color: '#fff',
                  padding: '10px 14px',
                  borderRadius: '14px 14px 4px 14px',
                  fontSize: 13.5,
                  lineHeight: '20px',
                  animation: instant ? 'none' : 'om-rise .3s ease both',
                }}
              >
                {s.text}
              </div>
            );
          if (s.role === 'assistant')
            return (
              <div
                key={i}
                style={{
                  alignSelf: 'flex-start',
                  maxWidth: '88%',
                  flexShrink: 0,
                  background: '#1c1e29',
                  color: '#cdd2dc',
                  padding: '10px 14px',
                  borderRadius: '14px 14px 14px 4px',
                  fontSize: 13.5,
                  lineHeight: '20px',
                  border: '1px solid #2c2f3d',
                  animation: instant ? 'none' : 'om-rise .3s ease both',
                }}
              >
                {s.text}
              </div>
            );
          return <ToolCard key={i} step={s} animate={!instant} defaultOpen={!collapsedTools} />;
        })}
        {typing && (
          <div
            style={{
              alignSelf: 'flex-start',
              background: '#1c1e29',
              border: '1px solid #2c2f3d',
              padding: '12px 14px',
              borderRadius: 14,
              display: 'flex',
              gap: 4,
            }}
          >
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                style={{ width: 6, height: 6, borderRadius: '50%', background: '#7c879c', animation: `om-dot 1.2s ${i * 0.15}s infinite` }}
              />
            ))}
          </div>
        )}
        {summary && done && (
          <div
            style={{
              alignSelf: 'center',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 7,
              background: 'rgba(143,214,168,.12)',
              color: '#8fd6a8',
              padding: '6px 12px',
              borderRadius: 99,
              fontSize: 12,
              fontWeight: 600,
              marginTop: 4,
            }}
          >
            <Icon name="checked-circle-fill" size={14} color="#8fd6a8" />3 tool calls · payment scheduled
          </div>
        )}
      </div>
    </div>
  );
}

// ---- MCP tools ----------------------------------------------------------
const MCP_TOOLS: Array<[string, string, string]> = [
  ['list_businesses', 'GET', 'Discover the businesses the key can act on'],
  ['check_eligibility', 'GET', 'See if a business can pay + what KYC is missing'],
  ['list_funding_sources', 'GET', 'List bank accounts available to debit'],
  ['create_funding_source', 'POST', 'Attach an ACH funding source'],
  ['list_delivery_methods', 'GET', 'Find or list payees (ACH / check)'],
  ['create_delivery_method', 'POST', 'Add a payee to send money to'],
  ['create_payment', 'POST', 'Schedule a payment (idempotent)'],
  ['get_payment', 'GET', 'Track a payment through its lifecycle'],
];

interface AgentsHubProps {
  go: Go;
}

function AgentsHub({ go }: AgentsHubProps) {
  const app = useApp();
  return (
    <div>
      {/* Hero band (dark) */}
      <div style={{ background: 'linear-gradient(160deg, #1c1428, #16171f 65%)', borderBottom: '1px solid #2c2f3d' }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', padding: '56px 28px' }} className="grid-agents">
          <div>
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 7,
                padding: '4px 11px',
                borderRadius: 99,
                background: 'rgba(183,158,255,.16)',
                color: '#cbb6ff',
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '.04em',
                textTransform: 'uppercase',
                marginBottom: 18,
              }}
            >
              <Icon name="ai-fill" size={14} color="#cbb6ff" />
              AI & agents
            </div>
            <h1 style={{ color: '#fff', fontSize: 44, lineHeight: '50px', letterSpacing: '-0.025em', margin: 0 }}>
              An API your agents
              <br />
              can actually use
            </h1>
            <p style={{ color: '#9aa3b2', fontSize: 17, lineHeight: '26px', margin: '18px 0 28px', maxWidth: 460 }}>
              Plug in the Melio MCP server and any AI assistant — Claude, Cursor, your own agent — can onboard businesses and move money
              through the same typed, idempotent API, with guardrails you set.
            </p>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Button size="large" variant="invert" leftIcon="add" onClick={() => go('/onboarding')}>
                Get an agent key
              </Button>
              <Button
                size="large"
                variant="ghost"
                style={{ color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
                rightIcon="arrow-right"
                onClick={() => go('/reference')}
              >
                Read the docs
              </Button>
            </div>
          </div>
          <AgentChat />
        </div>
      </div>

      <div style={{ maxWidth: 1180, margin: '0 auto', padding: '56px 28px' }}>
        {/* MCP setup */}
        <SectionHead
          eyebrow="Model Context Protocol"
          title="One config line, eight tools"
          sub="The Melio MCP server wraps the Melio API as agent tools. Drop it into any MCP-compatible client."
        />
        <div className="grid-2" style={{ gridTemplateColumns: '1fr 1fr', alignItems: 'start' }}>
          <div>
            <Segmented
              value="claude"
              onChange={() => {}}
              options={[{ value: 'claude', label: 'Claude Desktop' }]}
              style={{ marginBottom: 12 }}
            />
            <CodeBlock
              dark
              title="claude_desktop_config.json"
              maxHeight={260}
              code={`{
  "mcpServers": {
    "melio": {
      "command": "npx",
      "args": ["-y", "@melio/mcp"],
      "env": {
        "MELIO_API_KEY": "${app.apiKey}"
      }
    }
  }
}`}
            />
            <div style={{ marginTop: 12, display: 'flex', gap: 10 }}>
              <CopyChip text="npx -y @melio/mcp" dark={false} label="npx -y @melio/mcp" />
              <NakedButton rightIcon="arrow-right" onClick={() => go('/reference')}>
                Cursor & others
              </NakedButton>
            </div>
          </div>
          <Card padding={8}>
            {MCP_TOOLS.map(([name, m, desc], i) => (
              <div
                key={name}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '11px 12px',
                  borderTop: i ? '1px solid var(--border-static)' : 'none',
                }}
              >
                <Method m={m} style={{ minWidth: 38, textAlign: 'center' }} />
                <code className="om-mono" style={{ fontSize: 13, fontWeight: 600, minWidth: 168 }}>
                  {name}
                </code>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{desc}</span>
              </div>
            ))}
          </Card>
        </div>

        {/* Agent-ready principles */}
        <div style={{ marginTop: 64 }}>
          <SectionHead eyebrow="Why it works for agents" title="Designed for autonomous callers" />
          <div className="grid-3">
            {(
              [
                [
                  'fast',
                  'Idempotent writes',
                  'Agents retry. An idempotencyKey guarantees a payment runs at most once, no matter how many times it’s called.',
                ],
                [
                  'error',
                  'Structured errors',
                  'Every failure is a typed code with machine-readable details — so an agent can self-correct instead of guessing.',
                ],
                [
                  'document',
                  'llms.txt + typed schemas',
                  'A curated llms.txt and OpenAPI contract give models accurate, current context without scraping.',
                ],
                [
                  'checked-circle-fill',
                  'Deterministic test mode',
                  'Magic values force returns, voids, and KYC outcomes so agents can be evaluated reproducibly.',
                ],
                ['lock', 'Scoped agent keys', 'Issue read-only or pay-capped keys for agents, separate from your production secret.'],
                [
                  'to-do',
                  'Explicit state machine',
                  'A documented lifecycle (scheduled→sent→completed) means an agent always knows what’s next.',
                ],
              ] as Array<[string, string, string]>
            ).map(([ic, t, d]) => (
              <Card key={t} padding={22} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    background: 'var(--accent-soft)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon name={ic} size={22} color="var(--accent)" />
                </div>
                <div style={{ fontSize: 15.5, fontWeight: 600 }}>{t}</div>
                <div style={{ fontSize: 13.5, lineHeight: '20px', color: 'var(--text-secondary)' }}>{d}</div>
              </Card>
            ))}
          </div>
        </div>

        {/* Guardrails */}
        <div style={{ marginTop: 64 }}>
          <SectionHead
            eyebrow="Guardrails"
            title="Give agents power, keep the controls"
            sub="Scope what an agent can do at the key level. Set it once; the API enforces it on every call."
          />
          <Card padding={0} style={{ overflow: 'hidden' }}>
            <div className="grid-2" style={{ gridTemplateColumns: '1fr 1fr', gap: 0 }}>
              <div style={{ padding: 28 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <GuardRow
                    icon="lock"
                    title="Capability scope"
                    desc="Read-only, or allowed to create payments."
                    control={
                      <Pill kind="brand" dot={false}>
                        pay · capped
                      </Pill>
                    }
                  />
                  <GuardRow
                    icon="dollar"
                    title="Per-payment cap"
                    desc="Reject any single payment over the limit."
                    control={
                      <code className="om-mono" style={{ fontSize: 13, fontWeight: 600 }}>
                        $5,000.00
                      </code>
                    }
                  />
                  <GuardRow
                    icon="calendar"
                    title="Daily volume cap"
                    desc="Total an agent can move per day."
                    control={
                      <code className="om-mono" style={{ fontSize: 13, fontWeight: 600 }}>
                        $25,000.00
                      </code>
                    }
                  />
                  <GuardRow
                    icon="approval"
                    title="Require approval"
                    desc="Payments above the cap wait for a human."
                    control={<Toggle value={true} onChange={() => {}} />}
                  />
                </div>
              </div>
              <div style={{ padding: 28, background: '#16171f' }}>
                <div
                  className="om-mono"
                  style={{ fontSize: 11, color: '#5c6472', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.05em' }}
                >
                  agent key policy
                </div>
                <CodeBlock
                  dark
                  style={{ border: 'none' }}
                  code={jsonStr({
                    scope: ['payments:create', 'businesses:read'],
                    limits: { perPayment: 500000, perDay: 2500000 },
                    requireApprovalAbove: 500000,
                    environment: 'test',
                  })}
                  maxHeight={220}
                />
              </div>
            </div>
          </Card>
        </div>

        {/* CTA */}
        <div style={{ marginTop: 56, textAlign: 'center', padding: '40px 0' }}>
          <h2 style={{ fontSize: 28, letterSpacing: '-0.02em', margin: '0 0 12px' }}>Let an agent make its first payment</h2>
          <p style={{ fontSize: 16, color: 'var(--text-secondary)', margin: '0 auto 24px', maxWidth: 440 }}>
            Spin up a scoped test key and connect your MCP client in under five minutes.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
            <Button size="large" leftIcon="add" onClick={() => go('/onboarding')}>
              Get an agent key
            </Button>
            <Button size="large" variant="tertiary" rightIcon="arrow-right" onClick={() => go('/reference')}>
              Read the docs
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface GuardRowProps {
  icon: string;
  title: string;
  desc: string;
  control: ReactNode;
}

function GuardRow({ icon, title, desc, control }: GuardRowProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 10,
          background: 'var(--accent-soft)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon name={icon} size={20} color="var(--accent)" />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14.5, fontWeight: 600 }}>{title}</div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{desc}</div>
      </div>
      {control}
    </div>
  );
}

export { AgentsHub, AgentChat, ToolCard, GuardRow };
